import { motion } from "framer-motion";
import { MapPin, Clock, Phone, WhatsappLogo, NavigationArrow } from "@phosphor-icons/react";
import { SectionHead } from "@/components/site/Promotions";
import { BUSINESS } from "@/config";

export const Location = () => {
  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(BUSINESS.mapQuery)}&t=&z=14&ie=UTF8&iwloc=&output=embed`;
  const today = new Date().getDay(); // 0 Sun ... 6 Sat

  const isOpenRow = (i) => (i === 0 ? today >= 1 && today <= 5 : i === 1 ? today === 6 : today === 0);

  return (
    <section id="location" className="relative py-20 sm:py-28 bg-background z-10" data-testid="location">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead index="05" kicker="Visit The Shop" title="Roll In & Get Fitted" />

        <div className="grid lg:grid-cols-2 gap-6 mt-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="border border-border overflow-hidden min-h-[340px]"
          >
            <iframe
              title="Shop location"
              src={mapSrc}
              className="w-full h-full min-h-[340px] grayscale contrast-125"
              loading="lazy"
              data-testid="map-embed"
            />
          </motion.div>

          <div className="flex flex-col gap-4">
            <div className="border border-border bg-card p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <MapPin size={26} weight="duotone" className="text-primary shrink-0 mt-1" />
                <div>
                  <div className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">Address</div>
                  <p className="font-display font-bold text-2xl tracking-tight mt-1">{BUSINESS.address}</p>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(BUSINESS.mapQuery)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 text-sm font-bold text-primary hover:underline"
                    data-testid="directions-link"
                  >
                    <NavigationArrow size={16} weight="bold" /> Get directions
                  </a>
                </div>
              </div>
            </div>

            <div className="border border-border bg-card p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Clock size={22} weight="duotone" className="text-primary" />
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">Business Hours</div>
              </div>
              <ul className="divide-y divide-border">
                {BUSINESS.hours.map((h, i) => (
                  <li key={h.day} className="flex items-center justify-between py-3">
                    <span className="font-semibold flex items-center gap-2">
                      {isOpenRow(i) && <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
                      {h.day}
                    </span>
                    <span className={`text-sm ${h.time === "Closed" ? "text-muted-foreground" : "text-foreground/85"}`}>{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center justify-center gap-2 border border-border bg-card hover:border-primary/60 py-5 rounded-sm transition-colors" data-testid="location-call">
                <Phone size={22} weight="fill" className="text-primary" />
                <span className="font-bold text-sm uppercase tracking-wide">Call now</span>
              </a>
              <a href={`https://wa.me/${BUSINESS.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 border border-border bg-card hover:border-primary/60 py-5 rounded-sm transition-colors" data-testid="location-whatsapp">
                <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
                <span className="font-bold text-sm uppercase tracking-wide">WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
